import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, ShieldCheck, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/accept/${token}`);
        setInvite(data);
        setForm(f => ({ ...f, name: data.suggested_name || "" }));
      } catch (e) {
        setError(e?.response?.data?.detail || "Invalid or expired invite");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invites/accept/${token}`, form);
      localStorage.setItem("ld_token", data.token);
      localStorage.setItem("ld_user", JSON.stringify(data.user));
      toast.success(`Welcome to ${invite.business_name}!`);
      window.location.href = "/";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--brand)" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--bg)" }}>
        <Card className="p-10 max-w-md w-full text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <XCircle className="w-14 h-14 mx-auto text-red-500" />
          <h2 className="display text-2xl mt-4">Invite unavailable</h2>
          <p className="text-sm mt-2" style={{ color: "var(--text-soft)" }}>{error}</p>
          <Button className="mt-6 text-white" style={{ background: "var(--brand)" }} onClick={() => nav("/signin")}>Go to sign in</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--bg)" }} data-testid="invite-accept-page">
      <Card className="p-10 max-w-md w-full" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md flex items-center justify-center text-white" style={{ background: "var(--brand)" }}>
            <Sparkles className="w-5 h-5" />
          </div>
          <span className="display text-2xl">LaunchDeck</span>
        </div>
        <div className="mb-6">
          <div className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full mb-3" style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
            <ShieldCheck className="w-3 h-3" /> INVITED
          </div>
          <h1 className="display text-3xl mb-2">Join {invite.business_name}</h1>
          <p className="text-sm" style={{ color: "var(--text-soft)" }}>
            <strong>{invite.inviter_name}</strong> invited you to join as a <strong className="capitalize">{invite.role}</strong>. Set your password to get started.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input value={invite.email} disabled data-testid="invite-email" />
          </div>
          <div>
            <Label>Your name</Label>
            <Input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Full name" />
          </div>
          <div>
            <Label>Create password</Label>
            <Input data-testid="invite-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required placeholder="At least 6 characters" minLength={6} />
          </div>
          <Button data-testid="invite-submit" type="submit" disabled={submitting} className="w-full text-white" style={{ background: "var(--brand)" }}>
            {submitting ? "Creating account…" : `Join ${invite.business_name}`}
          </Button>
        </form>
      </Card>
    </div>
  );
}
